/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import BreakdownSites from '../../../shared/BreakdownSites/BreakdownSites';
import { EnergyUsageField } from '../../../shared/Enums';
import '../PortfolioEnergyUsage/EnergyUsageComponent/EnergyUsageComponent.scss';
import { IEmaaSDataProps } from '../IPortfolioProps';
import {
  convertDataForEui,
  getSiteListForEui
} from '../../../shared/Utils/portfolioDataFormatter';
import { IMSEMaaSPortfolioFormattedEuiData } from '../IEnergyUsagePortfolioProps';
import { ErrorContainer } from '@bmsp/bmsp.web.component.common';
import { apiNoContentStatusCode } from '../../../shared/Utils/Constants';
import ProgressBarWidget from '../../../shared/ProgressBar/ProgressBarWidget';
import { getEuiUnitDisplayName } from '../../../shared/Utils/unitHandling/EuiUnitHandling';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import { Loader } from '@scuf/common';

export const PortfolioEuiComponent = (props: IEmaaSDataProps) => {
  const {
    data: { emaasEuiDataList, euiErrors },
    visibleSites,
    preferredEuiUnit,
    onShowMore,
    maxRecords
  } = props;
  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  const [loader, setLoader] = React.useState(true);

  useEffect(() => {
    if (emaasEuiDataList && emaasEuiDataList.length > 0) {
      setLoader(false);
    }
  }, [emaasEuiDataList]);

  useEffect(() => {
    if (euiErrors && euiErrors !== null) {
      setLoader(false);
    }
  }, [euiErrors]);

  //Show error based on status code
  if (euiErrors && euiErrors.status) {
    if (euiErrors.status === apiNoContentStatusCode) {
      return (
        <div className="emaas-no-content-error">{t(d.NODATAAVAILABLE)}</div>
      );
    }
    return (
      <div className="emaas-no-content-error">
        <ErrorContainer
          headerText={'Loading Error'}
          errorDescription={
            'Refresh the page to load the data. Contact your administrator if the problem persists.'
          }
        />
      </div>
    );
  }

  let siteEmissionData: IMSEMaaSPortfolioFormattedEuiData = {
    tableData: [],
    progressBarData: undefined
  };
  let siteList = getSiteListForEui(emaasEuiDataList!);
  if (siteList && siteList.length > 0) {
    siteEmissionData = convertDataForEui(
      siteList,
      emaasEuiDataList![0].euiMeasurementUnit,
      preferredEuiUnit || '',
      visibleSites
    );
  }

  const headers = [
    {
      HeaderValue: t(d.SITENAMESHEADER),
      TextAlignValue: 'left',
      Field: 'SiteName',
      initialWidth: '35%'
    },
    {
      HeaderValue: t(d.EUI, {
        preferredEuiUnit: getEuiUnitDisplayName(preferredEuiUnit)
      }),
      TextAlignValue: 'right',
      Field: 'Value',
      sortable: true,
      initialWidth: '25%',
      className: 'eui-unit-display-header'
    },
    {
      HeaderValue: t(d.CHANGE),
      TextAlignValue: 'right',
      Field: 'Deviation',
      sortable: true,
      initialWidth: '25%'
    }
  ];

  if (siteEmissionData) {
    const { tableData, progressBarData } = siteEmissionData;
    if (tableData && progressBarData) {
      const {
        actualEui,
        baselineEui,
        euiDeviation,
        euiDeviationPercentage
      } = progressBarData;

      const aggregationEui = {
        actual: actualEui,
        previousPeriod: baselineEui,
        savings: euiDeviation!,
        savingsPercentage: euiDeviationPercentage!
      };
      return loader ? (
        <Loader />
      ) : (
        <div className="kpi-card-flex-container Eui-Portfolio-Card">
          <ProgressBarWidget
            //unit={emaasEuiDataList[0].euiMeasurementUnit + '/sqft'}
            unit={getEuiUnitDisplayName(preferredEuiUnit)}
            category={EnergyUsageField.EUI}
            aggregationData={aggregationEui}
          />
          <div className="kpi-card-breakdown-site-container">
            <BreakdownSites
              columnHeaders={headers as IDataTableHeader[]}
              meterData={tableData}
              onShowMore={onShowMore}
              maxRecords={maxRecords}
              activeWidgetName={'eui'}
            />
          </div>
        </div>
      );
    }
  }
  return loader ? (
    <Loader />
  ) : (
    <div className="noDataDisplay ms-common-error-container">
      <ErrorContainer
        headerText={'Loading Error'}
        errorDescription={
          'Refresh the page to load the data. Contact your administrator if the problem persists.'
        }
      />
    </div>
  );
};
export default PortfolioEuiComponent;
